import { CheckCircle2, Info, Loader2 } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { useToast } from "@/hooks/use-toast";
import type {
	SystemTerminologyConfigurationApi,
	UpdateSystemTerminologyConfigurationRequest,
} from "../../api/models/systemTerminologyConfiguration";
import {
	getSystemTerminologyConfiguration,
	updateSystemTerminologyConfiguration,
} from "../../api/services/systemTerminologyConfigurationService";
import {
	getActionsTermFromConfig,
	getCashDurationTermFromConfig,
	getCashMovementTermsFromConfig,
	getForecastTermFromConfig,
	getScenarioTermFromConfig,
	getSystemTerminology,
	getVarianceTermFromConfig,
	type SystemTerminologyConfig,
	updateSystemTerminology,
} from "../../lib/terminology-config";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";

// Map API response to UI model
function mapApiToUiModel(
	api: SystemTerminologyConfigurationApi,
): SystemTerminologyConfig {
	const terminologies = api.terminologies;
	return {
		cashDuration: (terminologies["cash-duration"] ??
			"coverage") as SystemTerminologyConfig["cashDuration"],
		cashDurationCustom: terminologies["cash-duration-custom"] ?? undefined,
		cashMovement: (terminologies["cash-movement"] ??
			"incoming-outgoing") as SystemTerminologyConfig["cashMovement"],
		cashMovementInCustom: terminologies["cash-movement-custom-in"] ?? undefined,
		cashMovementOutCustom:
			terminologies["cash-movement-custom-out"] ?? undefined,
		forecastTerm: (terminologies["forecast-term"] ??
			"forecast") as SystemTerminologyConfig["forecastTerm"],
		forecastTermCustom: terminologies["forecast-term-custom"] ?? undefined,
		scenarioTerm: (terminologies["scenario-term"] ??
			"scenarios") as SystemTerminologyConfig["scenarioTerm"],
		scenarioTermCustom: terminologies["scenario-term-custom"] ?? undefined,
		varianceTerm: (terminologies["variance-term"] ??
			"variance") as SystemTerminologyConfig["varianceTerm"],
		varianceTermCustom: terminologies["variance-term-custom"] ?? undefined,
		actionsTerm: (terminologies["actions-term"] ??
			"actions") as SystemTerminologyConfig["actionsTerm"],
		actionsTermCustom: terminologies["actions-term-custom"] ?? undefined,
	};
}

// Map UI model to API request
function mapUiModelToApiRequest(
	ui: SystemTerminologyConfig,
): UpdateSystemTerminologyConfigurationRequest {
	return {
		cashDuration: ui.cashDuration,
		cashDurationCustom: ui.cashDurationCustom ?? null,
		cashMovement: ui.cashMovement,
		cashMovementCustomIn: ui.cashMovementInCustom ?? null,
		cashMovementCustomOut: ui.cashMovementOutCustom ?? null,
		forecastTerm: ui.forecastTerm,
		forecastTermCustom: ui.forecastTermCustom ?? null,
		scenarioTerm: ui.scenarioTerm,
		scenarioTermCustom: ui.scenarioTermCustom ?? null,
		varianceTerm: ui.varianceTerm,
		varianceTermCustom: ui.varianceTermCustom ?? null,
		actionsTerm: ui.actionsTerm,
		actionsTermCustom: ui.actionsTermCustom ?? null,
	};
}

export const SystemTerminologySettings: React.FC = () => {
	const { toast } = useToast();
	const { mode } = useApp();
	const [config, setConfig] = useState<SystemTerminologyConfig>(
		getSystemTerminology(),
	);
	const [hasChanges, setHasChanges] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Load data based on mode
	useEffect(() => {
		const loadData = async () => {
			if (mode === "live") {
				setIsLoading(true);
				setError(null);
				try {
					const apiResponse = await getSystemTerminologyConfiguration();
					const uiModel = mapApiToUiModel(apiResponse);
					setConfig(uiModel);
				} catch (err) {
					setError(
						err instanceof Error ? err.message : "Failed to load configuration",
					);
				} finally {
					setIsLoading(false);
				}
			} else {
				// Demo mode - use localStorage
				setConfig(getSystemTerminology());
				setError(null);
			}
			setHasChanges(false);
		};

		loadData();
	}, [mode]);

	const handleCashDurationChange = (value: string) => {
		setConfig({
			...config,
			cashDuration: value as SystemTerminologyConfig["cashDuration"],
		});
		setHasChanges(true);
	};

	const handleCashMovementChange = (value: string) => {
		setConfig({
			...config,
			cashMovement: value as SystemTerminologyConfig["cashMovement"],
		});
		setHasChanges(true);
	};

	const handleForecastTermChange = (value: string) => {
		setConfig({
			...config,
			forecastTerm: value as SystemTerminologyConfig["forecastTerm"],
		});
		setHasChanges(true);
	};

	const handleScenarioTermChange = (value: string) => {
		setConfig({
			...config,
			scenarioTerm: value as SystemTerminologyConfig["scenarioTerm"],
		});
		setHasChanges(true);
	};

	const handleVarianceTermChange = (value: string) => {
		setConfig({
			...config,
			varianceTerm: value as SystemTerminologyConfig["varianceTerm"],
		});
		setHasChanges(true);
	};

	const handleActionsTermChange = (value: string) => {
		setConfig({
			...config,
			actionsTerm: value as SystemTerminologyConfig["actionsTerm"],
		});
		setHasChanges(true);
	};

	const handleSave = async () => {
		if (mode === "live") {
			setIsLoading(true);
			setError(null);
			try {
				const request = mapUiModelToApiRequest(config);
				const apiResponse = await updateSystemTerminologyConfiguration(request);
				const uiModel = mapApiToUiModel(apiResponse);
				setConfig(uiModel);
				setHasChanges(false);
				toast({
					title: "System Terminology Updated",
					description:
						"Changes have been saved to the database and applied to all users.",
				});
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : "Failed to save configuration";
				setError(errorMessage);
				toast({
					title: "Error",
					description: errorMessage,
					variant: "destructive",
				});
			} finally {
				setIsLoading(false);
			}
		} else {
			// Demo mode - save to sessionStorage (resets on hard refresh)
			updateSystemTerminology(config);
			setHasChanges(false);
			toast({
				title: "System Terminology Updated",
				description:
					"Changes are active for this session and will reset on page refresh.",
			});
		}
	};

	const handleCancel = () => {
		if (mode === "live") {
			// Reload from API
			const loadData = async () => {
				setIsLoading(true);
				try {
					const apiResponse = await getSystemTerminologyConfiguration();
					const uiModel = mapApiToUiModel(apiResponse);
					setConfig(uiModel);
				} catch (err) {
					setError(
						err instanceof Error
							? err.message
							: "Failed to reload configuration",
					);
				} finally {
					setIsLoading(false);
				}
			};
			loadData();
		} else {
			setConfig(getSystemTerminology());
		}
		setHasChanges(false);
	};

	// Preview values - use local config state for real-time updates
	const previewCashDuration = getCashDurationTermFromConfig(config, 6);
	const previewMovementTerms = getCashMovementTermsFromConfig(config);
	const previewForecastTerm = getForecastTermFromConfig(config);
	const previewScenarioTerm = getScenarioTermFromConfig(config);
	const previewVarianceTerm = getVarianceTermFromConfig(config);
	const previewActionsTerm = getActionsTermFromConfig(config);

	if (isLoading && !hasChanges) {
		return (
			<div className="flex items-center justify-center p-12">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				<span className="ml-2 text-muted-foreground">
					Loading configuration...
				</span>
			</div>
		);
	}

	if (error && mode === "live" && !hasChanges) {
		return (
			<div className="flex items-center justify-center p-12">
				<div className="text-center space-y-4">
					<Alert variant="destructive" className="max-w-md">
						<AlertDescription>{error}</AlertDescription>
					</Alert>
					<Button
						onClick={() => {
							setError(null);
							const reload = async () => {
								setIsLoading(true);
								try {
									const apiResponse = await getSystemTerminologyConfiguration();
									const uiModel = mapApiToUiModel(apiResponse);
									setConfig(uiModel);
									setError(null);
								} catch (err) {
									setError(
										err instanceof Error
											? err.message
											: "Failed to load configuration",
									);
								} finally {
									setIsLoading(false);
								}
							};
							reload();
						}}
					>
						Retry
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6 p-6">
			<div>
				<h1 className="text-2xl font-bold text-gray-900">System Terminology Configuration</h1>
				<p className="text-muted-foreground mt-1">
					Configure terminology used throughout the application for all users
				</p>
				<div className="mt-2">
					<span
						className={`text-xs px-2 py-1 rounded ${mode === "live" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
					>
						{mode === "live"
							? "Live Mode - Changes saved to database"
							: "Demo Mode - Changes reset on page refresh"}
					</span>
				</div>
			</div>

			{error && (
				<Alert variant="destructive">
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			)}

			{hasChanges && (
				<Alert>
					<Info className="h-4 w-4" />
					<AlertDescription>
						You have unsaved changes. Click "Save System Terminology" to apply
						them.
					</AlertDescription>
				</Alert>
			)}

			{/* Cash Duration Terminology */}
			<Card>
				<CardHeader>
					<CardTitle>Cash Duration Terminology</CardTitle>
					<p className="text-sm text-muted-foreground">
						How should the system describe cash duration/runway?
					</p>
				</CardHeader>
				<CardContent className="space-y-4">
					<RadioGroup
						value={config.cashDuration}
						onValueChange={handleCashDurationChange}
					>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="runway" id="runway" />
							<Label htmlFor="runway" className="font-normal cursor-pointer">
								Runway → "6 months runway"
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="coverage" id="coverage" />
							<Label
								htmlFor="coverage"
								className="font-normal cursor-pointer flex items-center gap-2"
							>
								Coverage → "6 months coverage"
								<span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
									RECOMMENDED
								</span>
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="reserve" id="reserve" />
							<Label htmlFor="reserve" className="font-normal cursor-pointer">
								Reserve → "6 months reserve"
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="buffer" id="buffer" />
							<Label htmlFor="buffer" className="font-normal cursor-pointer">
								Operating Buffer → "6 months operating buffer"
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="date" id="date" />
							<Label htmlFor="date" className="font-normal cursor-pointer">
								Cash Until Date → "Cash until 18 May 2026"
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="custom" id="custom-duration" />
							<Label
								htmlFor="custom-duration"
								className="font-normal cursor-pointer"
							>
								Custom Term:
							</Label>
							<Input
								placeholder="Enter custom term"
								className="max-w-xs"
								value={config.cashDurationCustom || ""}
								onChange={(e) => {
									setConfig({ ...config, cashDurationCustom: e.target.value });
									setHasChanges(true);
								}}
								disabled={config.cashDuration !== "custom"}
							/>
						</div>
					</RadioGroup>
				</CardContent>
			</Card>

			{/* Cash Movement Terminology */}
			<Card>
				<CardHeader>
					<CardTitle>Cash Movement Terminology</CardTitle>
					<p className="text-sm text-muted-foreground">
						How should the system describe cash inflows and outflows?
					</p>
				</CardHeader>
				<CardContent className="space-y-4">
					<RadioGroup
						value={config.cashMovement}
						onValueChange={handleCashMovementChange}
					>
						<div className="flex items-center space-x-2">
							<RadioGroupItem
								value="incoming-outgoing"
								id="incoming-outgoing"
							/>
							<Label
								htmlFor="incoming-outgoing"
								className="font-normal cursor-pointer flex items-center gap-2"
							>
								Incoming / Outgoing
								<span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
									RECOMMENDED
								</span>
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="inflows-outflows" id="inflows-outflows" />
							<Label
								htmlFor="inflows-outflows"
								className="font-normal cursor-pointer"
							>
								Inflows / Outflows
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem
								value="receipts-payments"
								id="receipts-payments"
							/>
							<Label
								htmlFor="receipts-payments"
								className="font-normal cursor-pointer"
							>
								Receipts / Payments
							</Label>
						</div>
						<div className="space-y-2">
							<div className="flex items-center space-x-2">
								<RadioGroupItem value="custom" id="custom-movement" />
								<Label
									htmlFor="custom-movement"
									className="font-normal cursor-pointer"
								>
									Custom Terms:
								</Label>
							</div>
							<div className="flex gap-2 ml-6">
								<Input
									placeholder="Incoming term"
									className="max-w-xs"
									value={config.cashMovementInCustom || ""}
									onChange={(e) => {
										setConfig({
											...config,
											cashMovementInCustom: e.target.value,
										});
										setHasChanges(true);
									}}
									disabled={config.cashMovement !== "custom"}
								/>
								<span className="self-center">/</span>
								<Input
									placeholder="Outgoing term"
									className="max-w-xs"
									value={config.cashMovementOutCustom || ""}
									onChange={(e) => {
										setConfig({
											...config,
											cashMovementOutCustom: e.target.value,
										});
										setHasChanges(true);
									}}
									disabled={config.cashMovement !== "custom"}
								/>
							</div>
						</div>
					</RadioGroup>
				</CardContent>
			</Card>

			{/* Forecast Terminology */}
			<Card>
				<CardHeader>
					<CardTitle>Forecast Terminology</CardTitle>
					<p className="text-sm text-muted-foreground">
						What term should be used for financial forecasts?
					</p>
				</CardHeader>
				<CardContent className="space-y-4">
					<RadioGroup
						value={config.forecastTerm}
						onValueChange={handleForecastTermChange}
					>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="forecast" id="forecast" />
							<Label
								htmlFor="forecast"
								className="font-normal cursor-pointer flex items-center gap-2"
							>
								Forecast
								<span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
									DEFAULT
								</span>
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="projection" id="projection" />
							<Label
								htmlFor="projection"
								className="font-normal cursor-pointer"
							>
								Projection
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="outlook" id="outlook" />
							<Label htmlFor="outlook" className="font-normal cursor-pointer">
								Outlook
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="plan" id="plan" />
							<Label htmlFor="plan" className="font-normal cursor-pointer">
								Plan
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="custom" id="custom-forecast" />
							<Label
								htmlFor="custom-forecast"
								className="font-normal cursor-pointer"
							>
								Custom Term:
							</Label>
							<Input
								placeholder="Enter custom term"
								className="max-w-xs"
								value={config.forecastTermCustom || ""}
								onChange={(e) => {
									setConfig({ ...config, forecastTermCustom: e.target.value });
									setHasChanges(true);
								}}
								disabled={config.forecastTerm !== "custom"}
							/>
						</div>
					</RadioGroup>
				</CardContent>
			</Card>

			{/* Scenario Terminology */}
			<Card>
				<CardHeader>
					<CardTitle>Scenario Terminology</CardTitle>
					<p className="text-sm text-muted-foreground">
						What term should be used for cash flow scenarios?
					</p>
				</CardHeader>
				<CardContent className="space-y-4">
					<RadioGroup
						value={config.scenarioTerm}
						onValueChange={handleScenarioTermChange}
					>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="scenarios" id="scenarios" />
							<Label
								htmlFor="scenarios"
								className="font-normal cursor-pointer flex items-center gap-2"
							>
								Scenarios
								<span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
									DEFAULT
								</span>
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="what-ifs" id="what-ifs" />
							<Label htmlFor="what-ifs" className="font-normal cursor-pointer">
								What-ifs
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="plans" id="scenario-plans" />
							<Label
								htmlFor="scenario-plans"
								className="font-normal cursor-pointer"
							>
								Plans
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="models" id="models" />
							<Label htmlFor="models" className="font-normal cursor-pointer">
								Models
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="custom" id="custom-scenario" />
							<Label
								htmlFor="custom-scenario"
								className="font-normal cursor-pointer"
							>
								Custom Term:
							</Label>
							<Input
								placeholder="Enter custom term"
								className="max-w-xs"
								value={config.scenarioTermCustom || ""}
								onChange={(e) => {
									setConfig({ ...config, scenarioTermCustom: e.target.value });
									setHasChanges(true);
								}}
								disabled={config.scenarioTerm !== "custom"}
							/>
						</div>
					</RadioGroup>
				</CardContent>
			</Card>

			{/* Variance Terminology */}
			<Card>
				<CardHeader>
					<CardTitle>Variance Terminology</CardTitle>
					<p className="text-sm text-muted-foreground">
						What term should be used to describe differences between forecast
						and actuals?
					</p>
				</CardHeader>
				<CardContent className="space-y-4">
					<RadioGroup
						value={config.varianceTerm}
						onValueChange={handleVarianceTermChange}
					>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="variance" id="variance" />
							<Label
								htmlFor="variance"
								className="font-normal cursor-pointer flex items-center gap-2"
							>
								Variance
								<span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
									DEFAULT
								</span>
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="deviation" id="deviation" />
							<Label htmlFor="deviation" className="font-normal cursor-pointer">
								Deviation
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="difference" id="difference" />
							<Label
								htmlFor="difference"
								className="font-normal cursor-pointer"
							>
								Difference
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="delta" id="delta" />
							<Label htmlFor="delta" className="font-normal cursor-pointer">
								Delta
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="custom" id="custom-variance" />
							<Label
								htmlFor="custom-variance"
								className="font-normal cursor-pointer"
							>
								Custom Term:
							</Label>
							<Input
								placeholder="Enter custom term"
								className="max-w-xs"
								value={config.varianceTermCustom || ""}
								onChange={(e) => {
									setConfig({ ...config, varianceTermCustom: e.target.value });
									setHasChanges(true);
								}}
								disabled={config.varianceTerm !== "custom"}
							/>
						</div>
					</RadioGroup>
				</CardContent>
			</Card>

			{/* Actions Terminology */}
			<Card>
				<CardHeader>
					<CardTitle>Actions Terminology</CardTitle>
					<p className="text-sm text-muted-foreground">
						What term should be used for follow-up actions and workflow items?
					</p>
				</CardHeader>
				<CardContent className="space-y-4">
					<RadioGroup
						value={config.actionsTerm}
						onValueChange={handleActionsTermChange}
					>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="actions" id="actions" />
							<Label
								htmlFor="actions"
								className="font-normal cursor-pointer flex items-center gap-2"
							>
								Actions
								<span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
									DEFAULT
								</span>
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="tasks" id="tasks" />
							<Label htmlFor="tasks" className="font-normal cursor-pointer">
								Tasks
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="to-dos" id="to-dos" />
							<Label htmlFor="to-dos" className="font-normal cursor-pointer">
								To-dos
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="follow-ups" id="follow-ups" />
							<Label
								htmlFor="follow-ups"
								className="font-normal cursor-pointer"
							>
								Follow-ups
							</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="custom" id="custom-actions" />
							<Label
								htmlFor="custom-actions"
								className="font-normal cursor-pointer"
							>
								Custom Term:
							</Label>
							<Input
								placeholder="Enter custom term"
								className="max-w-xs"
								value={config.actionsTermCustom || ""}
								onChange={(e) => {
									setConfig({ ...config, actionsTermCustom: e.target.value });
									setHasChanges(true);
								}}
								disabled={config.actionsTerm !== "custom"}
							/>
						</div>
					</RadioGroup>
				</CardContent>
			</Card>

			{/* Preview Section */}
			<Card className="border-blue-200 bg-blue-50/50">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Info className="h-5 w-5" />
						Live Preview
					</CardTitle>
					<p className="text-sm text-muted-foreground">
						See how your terminology choices will appear to users
					</p>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<div className="text-sm font-medium">Dashboard Card Preview:</div>
						<div className="p-4 bg-white border rounded-lg">
							<div className="text-2xl font-bold">£247,000</div>
							<div className="text-xs text-muted-foreground mt-1">
								{previewCashDuration}
							</div>
						</div>
					</div>

					<div className="space-y-2">
						<div className="text-sm font-medium">Chart Tooltip Preview:</div>
						<div className="p-4 bg-white border rounded-lg space-y-1 text-sm">
							<p className="font-bold">Balance: £125,000</p>
							<p className="text-green-600">
								{previewMovementTerms.in}: £23,989
							</p>
							<p className="text-red-600">
								{previewMovementTerms.out}: £14,624
							</p>
						</div>
					</div>

					<div className="space-y-2">
						<div className="text-sm font-medium">Chart Title Preview:</div>
						<div className="p-4 bg-white border rounded-lg">
							<div className="font-semibold">
								90-Day Cash Flow {previewForecastTerm}
							</div>
						</div>
					</div>
					<div className="space-y-2">
						<div className="text-sm font-medium">Navigation Preview:</div>
						<div className="p-4 bg-white border rounded-lg space-y-1 text-sm">
							<p className="font-medium">{previewScenarioTerm}</p>
							<p className="font-medium">{previewVarianceTerm} Analysis</p>
							<p className="font-medium">{previewActionsTerm}</p>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Save Button */}
			<Card className="border-amber-200 bg-amber-50/50">
				<CardContent className="pt-6">
					<div className="space-y-4">
						<div>
							<p className="font-medium text-amber-900">
								{mode === "live"
									? "Saving will update terminology for all users immediately"
									: "Demo Mode: Changes are active for this session only and will reset on page refresh"}
							</p>
							<p className="text-sm text-amber-700 mt-1">
								{mode === "live"
									? "All users will see the new terminology the next time they refresh or navigate."
									: "Switch to Live Mode to save changes to the database for all users."}
							</p>
						</div>
					</div>
					<div className="flex gap-3 mt-4">
						<Button
							onClick={handleSave}
							disabled={!hasChanges || isLoading}
							className="flex items-center gap-2"
						>
							{isLoading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<CheckCircle2 className="h-4 w-4" />
							)}
							Save System Terminology
						</Button>
						{hasChanges && (
							<Button
								variant="outline"
								onClick={handleCancel}
								disabled={isLoading}
							>
								Cancel
							</Button>
						)}
					</div>
				</CardContent>
			</Card>
		</div>
	);
};
